import React from "react";
import type { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid-pro";
import { DataGrid } from "../../../shared/components/DataGrid";
import { usePopoutWindowContext } from "../../../shared/components/PopoutWindow";
import { useAppSelector } from "../../../store";
import { type AggregateRow } from "../../../store/profiling/selectors";
import { filteredAggregateRows, filtersNeedTraces, hasActiveFilters, type ProfilingFilters } from "../../../store/profiling/filtering";
import type { Trace } from "../../../store/tracing/types";
import { formatMs } from "./viz";
import CodeBlock from "../CodeBlock";
import { CodeHoverLayer, type CodeHoverApi } from "../CodeHover";

// Right-aligned, tabular millisecond column; the raw numeric value drives sorting, `formatMs` only formats.
const msColumn = (field: keyof AggregateRow, headerName: string, description: string): GridColDef<AggregateRow> => ({
    field,
    headerName,
    description,
    type: "number",
    width: 96,
    valueFormatter: (value: number) => formatMs(value),
});

const EMPTY_TRACES: Trace[] = [];

type Props = {
    filters: ProfilingFilters;
    /** Toggles the given node source in the shared source filter (rows are clickable). */
    onToggleSource: (source: string) => void;
};

function AggregateView({ filters, onToggleSource }: Props) {
    const profiling = useAppSelector(s => s.profiling);
    const traces = useAppSelector(s => s.tracing.traces);
    // Aggregation only consults traces when an event filter is active; substituting a stable empty
    // array otherwise keeps live trace churn (one per canvas interaction) from re-aggregating every
    // retained node and re-rendering the grid.
    const rowTraces = filtersNeedTraces(filters) ? traces : EMPTY_TRACES;
    const rows = React.useMemo(() => filteredAggregateRows(profiling, rowTraces, filters), [profiling, rowTraces, filters]);
    // Portal the hover tooltip into the pop-out window's body so it renders in the correct window.
    const container = usePopoutWindowContext()?.window?.document.body ?? undefined;
    const hoverRef = React.useRef<CodeHoverApi>(null);

    const columns = React.useMemo<GridColDef<AggregateRow>[]>(
        () => [
            { field: "label", headerName: "Node", flex: 1, minWidth: 140, description: "One entry per template source location, aggregated across every retained render pass. Named after the node's JSX tag or component, or a snippet of the expression." },
            {
                field: "source",
                headerName: "Source",
                description: "The node's exact source text as written in the template. Hover a cell to preview the formatted source; click the row to filter passes and aggregate entries by this source.",
                sortable: false,
                flex: 2,
                minWidth: 260,
                renderCell: params => (
                    <span
                        className="agg-source-cell"
                        onClick={e => e.stopPropagation()}
                        onMouseMove={e => hoverRef.current?.move(params.row.source, e.clientX, e.clientY)}
                        onMouseLeave={() => hoverRef.current?.hide()}
                    >
                        <CodeBlock code={params.row.source} singleLine pinnable onPinToggle={() => hoverRef.current?.hide()} />
                    </span>
                ),
            },
            { field: "count", headerName: "Count", type: "number", width: 90, valueFormatter: (v: number) => (v ? v.toLocaleString() : "—"), description: "How many times the parser evaluated this node: once per template render plus once per callback re-evaluation, across all retained passes. A node inside a loop is counted once per iteration." },
            msColumn("totalSelfTime", "Σ self", "Total time the parser spent on this node itself — resolving its component and evaluating its attribute/expression bindings — excluding time spent in child nodes, which is attributed to their own rows."),
            msColumn("avgSelfTime", "Avg self", "Mean parser self time per evaluation (Σ self ÷ Count). A high average points at an expensive prop or expression; a high Σ self with a low average points at a node that is simply evaluated very often."),
            msColumn("totalTime", "Σ total", "Total inclusive parser time: this node plus its entire subtree. A container's Σ total includes all of its children's time, so it can far exceed its own Σ self."),
            { field: "reactCount", headerName: "Commits", type: "number", width: 96, valueFormatter: (v: number) => (v ? v.toLocaleString() : "—"), description: "Number of React commits that re-rendered this component (mounts and updates), across all retained passes." },
            msColumn("avgCommitTime", "Avg commit", "Mean React render duration per commit: the time React spent re-rendering this component (and its profiled descendants) in a single commit."),
            msColumn("totalCommitTime", "Σ commit", "Total React render duration across all commits of this component. Compare against Σ self to see whether the cost lies in evaluating the template or in React re-rendering the component."),
        ],
        []
    );

    const columnGroupingModel = React.useMemo<GridColumnGroupingModel>(
        () => [
            { groupId: "parser", headerName: "Parser", description: "Performance metrics captured by the JSX parser: how many times a node with this source code was evaluated (template renders and callback re-runs) and the time spent resolving it and evaluating its input props.", children: [{ field: "count" }, { field: "totalSelfTime" }, { field: "avgSelfTime" }, { field: "totalTime" }] },
            { groupId: "react", headerName: "React commit", description: "React render timings, captured via React.Profiler around each custom component — the parser columns measure evaluating the template, these measure the React re-renders it caused. Populated only while \"React render timing\" is enabled.", children: [{ field: "reactCount" }, { field: "avgCommitTime" }, { field: "totalCommitTime" }] },
        ],
        []
    );

    if (rows.length === 0) {
        return (
            <div className="aggregate-empty">
                {hasActiveFilters(filters) && Object.keys(profiling.cyclesById).length > 0
                    ? "No aggregate entries match the current filters."
                    : "No profiling data captured yet. Enable profiling and interact with the canvas."}
            </div>
        );
    }

    return (
        <div className="aggregate-view">
            <DataGrid
                className="aggregate-grid"
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                getRowId={r => r.key}
                onRowClick={params => onToggleSource(params.row.source)}
                getRowClassName={params => (filters.sources.includes(params.row.source) ? "source-filter-active" : "")}
                disableRowSelectionOnClick
                disableColumnMenu
                density="compact"
                rowHeight={36}
                initialState={{ sorting: { sortModel: [{ field: "totalSelfTime", sort: "desc" }] } }}
                sx={{ flex: 1, minHeight: 0 }}
            />
            <CodeHoverLayer ref={hoverRef} container={container} />
        </div>
    );
}

export default React.memo(AggregateView);
